// import React, { useEffect, useState } from "react";
// import { Spinner } from "react-bootstrap";
// import { useDispatch, useSelector } from "react-redux";
// import { Outlet, useNavigate } from "react-router-dom";
// import { NumberParam, useQueryParam } from "use-query-params";

// import { Pagination } from "src/components/Pagination";
// import { useIsMobile } from "src/hooks/useIsMobile";
// import { getUserData } from "src/redux/selectors/user";
// import { FirebaseDataService } from "src/services/Firebase/data";
// import { Client, ID } from "src/types";

// import { Content, ContentFrame, TopBar } from "src/styles";
// import BreadCrumbs from "../../components/BreadCrumbs";
// import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "../../components/ListScreen";
// import { compareBy } from "../../utils";
// import { setOpenedClientsPage as setPageStore } from "./../../redux/actions/openedPages";
// import { ClientTable } from "./ClientTable";
// import { ClientsList } from "./ClientsList";
// import { DEFAULT_PAGE_SIZE, INITIAL_LAST_PAGE, MANAGER_CLIENTS_BREADCRUMBS, tableHeaders } from "./constants";

// export const Clients = () => {
//   const { isMobile } = useIsMobile();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [clients, setClients] = useState<Client[]>([]);
//   const [currentClientsPage, setCurrentClientsPage] = useState<Client[]>([]);
//   const { id: employeeId } = useSelector(getUserData);

//   const [isLoading, setIsLoading] = useState(false);

//   const [openedClientsPage, setOpenedClientsPage] = useQueryParam("openedClientsPage", NumberParam);
//   const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
//   const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

//   const pageNum = openedClientsPage || 1;

//   const convertClientsObjectToArray = (clientsObject: any) => {
//     return Object.keys(clientsObject).reduce((acc: Client[], key, index) => {
//       if (clients[clients.length - 1]?.id && !index) return acc;

//       return [
//         ...acc,
//         {
//           id: key,
//           ...clientsObject[key],
//         },
//       ];
//     }, []);
//   };

//   const getPageSize = async () => {
//     try {
//       const { data } = await FirebaseDataService.getEmployeeClientsPageSize();
//       setPageSize(data || DEFAULT_PAGE_SIZE);
//     } catch (e) {
//       setPageSize(DEFAULT_PAGE_SIZE);
//     }
//   };

//   const getClients = async () => {
//     setIsLoading(true);

//     if (!lastPage || (lastPage && pageNum <= lastPage)) {
//       try {
//         const { data: clientsObject } = await FirebaseDataService.getEmployeeClients({
//           employeeId: employeeId.toString(),
//         });

//         const allClients: Client[] = convertClientsObjectToArray(clientsObject).sort((client1, client2) =>
//           compareBy("name", client1, client2),
//         );
//         const resultClientsPageArray = allClients.slice((pageNum - 1) * pageSize, pageNum * pageSize);

//         setCurrentClientsPage(resultClientsPageArray);
//         setClients(allClients);
//         setLastPage(Math.ceil(allClients.length / pageSize));
//       } catch (e) {
//         console.log(e.message);
//       }
//     }

//     setIsLoading(false);
//   };

//   const onPageChangeClick = (pageNumber: number) => {
//     setOpenedClientsPage(pageNumber);
//     dispatch(setPageStore(pageNumber));
//     setCurrentClientsPage(clients.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
//   };

//   const onRowClick = (id: ID) => navigate(`/clients/${id}`);

//   useEffect(() => {
//     setIsLoading(true);
//     getPageSize().finally(() => getClients());
//   }, [pageSize]);

//   const onClickBreadcrumb = (route: ID | null) => typeof route === "string" && navigate(route);

//   return (
//     <Container>
//       <TopBar>
//         <BreadCrumbs folderStack={MANAGER_CLIENTS_BREADCRUMBS} onClickItem={onClickBreadcrumb} />
//       </TopBar>

//       <Content>
//         <ContentFrame>
//           {isLoading ? (
//             <EmptyContainer>
//               <Spinner animation="border" size="sm" />
//             </EmptyContainer>
//           ) : (
//             <>
//               {currentClientsPage?.length ? (
//                 <>
//                   {isMobile ? (
//                     <ClientsList onClick={onRowClick} data={currentClientsPage} />
//                   ) : (
//                     <ClientTable
//                       onRowClick={onRowClick}
//                       isLoading={isLoading}
//                       headers={tableHeaders}
//                       data={currentClientsPage}
//                     />
//                   )}
//                 </>
//               ) : (
//                 <NoTicketsMessage $isMobile={isMobile}>No Clients assigned</NoTicketsMessage>
//               )}
//               <PaginationContainer>
//                 <Pagination
//                   totalCount={clients.length}
//                   pageSize={pageSize}
//                   onPageChange={onPageChangeClick}
//                   currentPage={pageNum}
//                 />
//               </PaginationContainer>
//             </>
//           )}
//         </ContentFrame>
//       </Content>
//       <Outlet />
//     </Container>
//   );
// };

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { NumberParam, useQueryParam } from "use-query-params";

import { Pagination } from "src/components/Pagination";
import { PaginationContainer } from "src/components/ListScreen";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { Client, ClientFormData, ClientRow, Employee, ID } from "src/types";

import { compareBy } from "../../utils";
import { setOpenedClientsPage as setPageStore } from "./../../redux/actions/openedPages";
import { DEFAULT_PAGE_SIZE, emptyDraft, INITIAL_LAST_PAGE } from "./constants";

const fmtDate = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

export const Clients = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [clients, setClients] = useState<Client[]>([]);
  const [currentClientsPage, setCurrentClientsPage] = useState<Client[]>([]);
  const [createClient, setCreateClient] = useState(false);
  const [draft, setDraft] = useState<ClientFormData>(emptyDraft);
  const [createError, setCreateError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { id: employeeId, role } = useSelector(getUserData);
  const [allManagers, setAllManagers] = useState<Employee[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [openedClientsPage, setOpenedClientsPage] = useQueryParam("openedClientsPage", NumberParam);
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const pageNum = openedClientsPage || 1;

  const convertObjectToArray = (clientsObject: any) => {
    return Object.keys(clientsObject).reduce((acc: any[], key, index) => {
      if (clients[clients.length - 1]?.id && !index) return acc;

      return [
        ...acc,
        {
          id: key,
          ...clientsObject[key],
        },
      ];
    }, []);
  };

  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getEmployeeClientsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const getAllManagers = async () => {
    try {
      const { data: managers } = await FirebaseDataService.getManagers();

      const allManagers: Employee[] = convertObjectToArray(managers).sort((client1, client2) =>
        compareBy("name", client1, client2),
      );

      setAllManagers(allManagers);
    } catch (e) {
      console.log(e.message);
    }
  };

  const getClients = async () => {
    setIsLoading(true);

    if (!lastPage || (lastPage && pageNum <= lastPage)) {
      try {
        console.log("Fetching clients for employeeId:", employeeId, "with role:", role);
        const { data: clientsObject } = await FirebaseDataService.getEmployeeClients({
          employeeId: employeeId.toString(),
          role: role,
        });

        const allClients: Client[] = convertObjectToArray(clientsObject).sort((client1, client2) =>
          compareBy("name", client1, client2),
        );

        console.log("Fetched clients:", allClients);

        const resultClientsPageArray = allClients.slice((pageNum - 1) * pageSize, pageNum * pageSize);

        setCurrentClientsPage(resultClientsPageArray);
        setClients(allClients);
        setLastPage(Math.ceil(allClients.length / pageSize));
      } catch (e) {
        console.log(e.message);
      }
    }

    setIsLoading(false);
  };

  const onPageChangeClick = (pageNumber: number) => {
    setOpenedClientsPage(pageNumber);
    dispatch(setPageStore(pageNumber));
    setCurrentClientsPage(clients.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
  };

  const onRowClick = (id: ID) => navigate(`/clients/${id}`);

  const onDraftChange = <K extends keyof ClientFormData>(key: K, value: ClientFormData[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const createClientRecord = async () => {
    const customerId = draft.customerId.trim();
    const companyName = draft.companyName?.trim();

    if (!customerId) {
      setCreateError("Customer ID is required.");
      return;
    }

    if (!companyName) {
      setCreateError("Company Name is required.");
      return;
    }

    if (draft.customerPortalAccess && !draft.portalLoginEmail?.trim()) {
      setCreateError("Portal login email is required when portal access is enabled.");
      return;
    }

    const newId = uuidv4();
    const now = new Date().toISOString();

    const payload: Record<string, unknown> = {
      id: newId,
      customerId: customerId,
      companyName: companyName,
      name: companyName,
      status: draft.status,
      managerId: employeeId?.toString() || "",
      createdBy: employeeId?.toString() || "",
      createdAt: now,
      updatedAt: now,
      changeDate: now,
      primaryContactName: draft.primaryContactName?.trim(),
      emailPrimary: draft.emailPrimary?.trim(),
      email: draft.emailPrimary?.trim(),
      phonePrimary: draft.phonePrimary?.trim(),
      phone: draft.phonePrimary?.trim(),
      accountManager: draft.accountManager?.trim(),
      customerPortalAccess: draft.customerPortalAccess,
      portalLoginEmail: draft.customerPortalAccess ? draft.portalLoginEmail?.trim() : "",
      billingAddressLine1: draft.billingAddressLine1?.trim(),
      billingAddressLine2: draft.billingAddressLine2?.trim(),
      city: draft.city?.trim(),
      stateProvince: draft.stateProvince?.trim(),
      postcode: draft.postcode?.trim(),
      country: draft.country?.trim(),
      paymentTerms: draft.paymentTerms?.trim(),
      contractType: draft.contractType?.trim(),
      slaTier: draft.slaTier?.trim(),
      currency: draft.currency?.trim(),
      taxId: draft.taxId?.trim(),
      emailBilling: draft.emailBilling?.trim(),
      customerType: draft.customerType?.trim(),
      industry: draft.industry?.trim(),
      tradingName: draft.tradingName?.trim(),
      contactTitle: draft.contactTitle?.trim(),
      phoneMobile: draft.phoneMobile?.trim(),
      notes: draft.notes?.trim(),
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") {
        delete payload[key];
      }
    });

    setCreateError("");
    setIsCreating(true);

    try {
      await FirebaseDataService.createClient(payload as unknown as ClientFormData, employeeId?.toString() || "");
      setDraft(emptyDraft);
      setCreateClient(false);
      await getClients();
    } catch (error: any) {
      setCreateError(error?.message || "Unable to create client.");
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getPageSize().finally(() => getClients());
    getAllManagers();
  }, [pageSize]);

  const avgScore =
    clients.length === 0
      ? 100
      : Math.round(clients.reduce((s, r) => s + Number((r as ClientRow).score ?? 100), 0) / clients.length);

  const totalSites = clients.reduce((a, r) => a + Number((r as ClientRow).siteCount ?? 0), 0);
  const atRiskCount = clients.filter((r) => Number((r as ClientRow).score ?? 100) < 80).length;

  return (
    <Page>
      <Header>
        <Eyebrow>Resources</Eyebrow>
        <TitleRow>
          <Title>Clients</Title>
          <Meta>{`${clients.length} client${clients.length === 1 ? "" : "s"} on record`}</Meta>
        </TitleRow>
      </Header>

      <TilesGrid>
        <SummaryCard>
          <SummaryLabel>Total Clients</SummaryLabel>
          <SummaryValue>{clients.length}</SummaryValue>
          <SummarySub>active relationships</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Avg Score</SummaryLabel>
          <SummaryValue>{avgScore}</SummaryValue>
          <SummarySub>across portfolio</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Sites</SummaryLabel>
          <SummaryValue>{totalSites}</SummaryValue>
          <SummarySub>across all clients</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>At Risk</SummaryLabel>
          <SummaryValue>{atRiskCount}</SummaryValue>
          <SummarySub>score &lt; 80</SummarySub>
        </SummaryCard>
      </TilesGrid>

      <ActionRow>
        <NewClientButton onClick={() => setCreateClient(!createClient)} type="button">
          {createClient ? "Cancel" : "+ New Client"}
        </NewClientButton>
      </ActionRow>

      {createClient && (
        <CreatePanel>
          <FormSectionHeader>
            <FormSectionIndex>01</FormSectionIndex>
            <div>
              <FormSectionTitle>Client details</FormSectionTitle>
              <FormSectionHelper>
                Capture required account details first. Optional billing and classification details can be added now or
                later.
              </FormSectionHelper>
            </div>
          </FormSectionHeader>

          <FormGrid>
            <Field>
              <Label>Customer ID *</Label>
              <Input
                value={draft.customerId}
                onChange={(e) => onDraftChange("customerId", e.target.value)}
                placeholder="e.g. ENBRIDGE"
              />
            </Field>

            <Field>
              <Label>Company Name *</Label>
              <Input
                value={draft.companyName}
                onChange={(e) => onDraftChange("companyName", e.target.value)}
                placeholder="e.g. Enbridge Energy"
              />
            </Field>

            <Field>
              <Label>Status *</Label>
              <Select
                value={draft.status}
                onChange={(e) => onDraftChange("status", e.target.value as "active" | "inactive")}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </Field>

            {/* <Field>
              <Label>Account Manager</Label>
              <Input
                value={draft.account_manager}
                onChange={(e) => onDraftChange("account_manager", e.target.value)}
                placeholder="Internal owner"
              />
            </Field> */}

            <Field>
              <Label>Account Manager</Label>
              <Select value={draft.accountManager} onChange={(e) => onDraftChange("accountManager", e.target.value)}>
                <option value="">-- Select Manager --</option>
                {allManagers.map((manager) => (
                  <option key={manager.id} value={manager.employeeId}>
                    {manager.firstName} {manager.lastName}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              <Label>Primary Contact Name</Label>
              <Input
                value={draft.primaryContactName}
                onChange={(e) => onDraftChange("primaryContactName", e.target.value)}
                placeholder="e.g. Sarah Thompson"
              />
            </Field>

            <Field>
              <Label>Contact Title</Label>
              <Input
                value={draft.contactTitle}
                onChange={(e) => onDraftChange("contactTitle", e.target.value)}
                placeholder="e.g. Facilities Manager"
              />
            </Field>

            <Field>
              <Label>Primary Email</Label>
              <Input
                type="email"
                value={draft.email}
                onChange={(e) => onDraftChange("email", e.target.value)}
                placeholder="name@company.com"
              />
            </Field>

            <Field>
              <Label>Primary Phone</Label>
              <Input
                value={draft.phone}
                onChange={(e) => onDraftChange("phone", e.target.value)}
                placeholder="e.g. +1 416 555 0199"
              />
            </Field>

            <Field>
              <Label>Mobile Phone</Label>
              <Input
                value={draft.phoneMobile}
                onChange={(e) => onDraftChange("phoneMobile", e.target.value)}
                placeholder="Optional"
              />
            </Field>

            <Field>
              <Label>Portal Access</Label>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={draft.customerPortalAccess}
                  onChange={(e) => onDraftChange("customerPortalAccess", e.target.checked)}
                />
                <span>Enable customer portal login</span>
              </CheckboxRow>
            </Field>

            <Field>
              <Label>Portal Login Email</Label>
              <Input
                type="email"
                value={draft.portalLoginEmail}
                onChange={(e) => onDraftChange("portalLoginEmail", e.target.value)}
                placeholder="Required if portal access is enabled"
                disabled={!draft.customerPortalAccess}
              />
            </Field>

            <Field>
              <Label>Customer Type</Label>
              <Input value={draft.customerType} onChange={(e) => onDraftChange("customerType", e.target.value)} />
            </Field>

            <Field>
              <Label>Industry</Label>
              <Input value={draft.industry} onChange={(e) => onDraftChange("industry", e.target.value)} />
            </Field>

            <Field>
              <Label>Trading Name</Label>
              <Input value={draft.tradingName} onChange={(e) => onDraftChange("tradingName", e.target.value)} />
            </Field>

            <Field>
              <Label>Billing Email</Label>
              <Input
                type="email"
                value={draft.emailBilling}
                onChange={(e) => onDraftChange("emailBilling", e.target.value)}
              />
            </Field>

            <Field>
              <Label>Billing Address Line 1</Label>
              <Input
                value={draft.billingAddressLine1}
                onChange={(e) => onDraftChange("billingAddressLine1", e.target.value)}
              />
            </Field>

            <Field>
              <Label>Billing Address Line 2</Label>
              <Input
                value={draft.billingAddressLine2}
                onChange={(e) => onDraftChange("billingAddressLine2", e.target.value)}
              />
            </Field>

            <Field>
              <Label>City</Label>
              <Input value={draft.city} onChange={(e) => onDraftChange("city", e.target.value)} />
            </Field>

            <Field>
              <Label>State / Province</Label>
              <Input value={draft.stateProvince} onChange={(e) => onDraftChange("stateProvince", e.target.value)} />
            </Field>

            <Field>
              <Label>Postal Code</Label>
              <Input value={draft.postcode} onChange={(e) => onDraftChange("postcode", e.target.value)} />
            </Field>

            <Field>
              <Label>Country</Label>
              <Input value={draft.country} onChange={(e) => onDraftChange("country", e.target.value)} />
            </Field>

            <Field>
              <Label>Payment Terms</Label>
              <Input value={draft.paymentTerms} onChange={(e) => onDraftChange("paymentTerms", e.target.value)} />
            </Field>

            <Field>
              <Label>Contract Type</Label>
              <Input value={draft.contractType} onChange={(e) => onDraftChange("contractType", e.target.value)} />
            </Field>

            <Field>
              <Label>SLA Tier</Label>
              <Input value={draft.slaTier} onChange={(e) => onDraftChange("slaTier", e.target.value)} />
            </Field>

            <Field>
              <Label>Currency</Label>
              <Input
                value={draft.currency}
                onChange={(e) => onDraftChange("currency", e.target.value)}
                placeholder="CAD"
              />
            </Field>

            <Field>
              <Label>Tax ID</Label>
              <Input value={draft.taxId} onChange={(e) => onDraftChange("taxId", e.target.value)} />
            </Field>

            <Field className="full">
              <Label>Notes</Label>
              <Textarea value={draft.notes} onChange={(e) => onDraftChange("notes", e.target.value)} rows={4} />
            </Field>
          </FormGrid>

          {createError && <FormError>{createError}</FormError>}

          <FormActions>
            <SecondaryButton
              type="button"
              onClick={() => {
                setDraft(emptyDraft);
                setCreateError("");
                setCreateClient(false);
              }}
            >
              Discard
            </SecondaryButton>
            <PrimaryButton type="button" onClick={createClientRecord} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create Client"}
            </PrimaryButton>
          </FormActions>
        </CreatePanel>
      )}

      <Panel>
        <PanelHeader>
          <PanelTitle>All Clients</PanelTitle>
          <PanelMeta>{`${currentClientsPage.length} shown`}</PanelMeta>
        </PanelHeader>

        {isLoading ? (
          <EmptyRow>Loading...</EmptyRow>
        ) : currentClientsPage.length === 0 ? (
          <EmptyRow>No clients assigned.</EmptyRow>
        ) : (
          <>
            <TableHeader>
              <span>ID</span>
              <span>Name</span>
              <span>Primary Contact</span>
              <span>Type</span>
              <Right>Sites</Right>
              <Right>Score</Right>
              <Right>Since</Right>
            </TableHeader>
            {currentClientsPage.map((c: ClientRow) => (
              <TableRow key={c.customerId} onClick={() => onRowClick(c.customerId)}>
                <IdPill>{c.customerId.slice(0, 6).toUpperCase()}</IdPill>
                <NameCell>{c.name}</NameCell>
                <MutedCell>{c.primaryContact ?? c.primary_contact ?? <ItalicDash>-</ItalicDash>}</MutedCell>
                <MutedCell>{c.type ?? "-"}</MutedCell>
                <Right>{c.siteCount ?? 0}</Right>
                <Right $strong={Number(c.score ?? 100) < 80}>{Number(c.score ?? 100).toFixed(0)}</Right>
                <RightSmall>
                  {fmtDate(c.relationshipStart ?? c.relationship_start ?? c.createdAt ?? c.created_at)}
                </RightSmall>
              </TableRow>
            ))}
          </>
        )}
      </Panel>

      <PaginationContainer>
        <Pagination
          totalCount={clients.length}
          pageSize={pageSize}
          onPageChange={onPageChangeClick}
          currentPage={pageNum}
        />
      </PaginationContainer>

      <Outlet />
    </Page>
  );
};

const Page = styled.div`
  padding: 24px 40px 0;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 32px;
  font-weight: 700;
`;

const Meta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 16px;
`;

const SummaryLabel = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const SummaryValue = styled.p`
  margin: 6px 0 2px;
  color: ${colors.black.dark1};
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const SummarySub = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const CreatePanel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 20px;
  margin-bottom: 24px;
`;

const FormSectionHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const FormSectionIndex = styled.span`
  font-size: 12px;
  line-height: 1;
  letter-spacing: 0.08em;
  color: ${colors.grey.dark6};
  font-weight: 700;
  margin-top: 2px;
`;

const FormSectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  color: ${colors.black.dark1};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-weight: 700;
`;

const FormSectionHelper = styled.p`
  margin: 4px 0 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;

  .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.grey.dark6};
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  min-height: 40px;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  font-size: 13px;
  padding: 8px 10px;

  &:focus {
    outline: none;
    border-color: ${colors.grey.dark3};
  }

  &:disabled {
    background: ${colors.grey.light6};
    color: ${colors.grey.dark6};
  }
`;

const Select = styled.select`
  width: 100%;
  min-height: 40px;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  font-size: 13px;
  padding: 8px 10px;

  &:focus {
    outline: none;
    border-color: ${colors.grey.dark3};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  font-size: 13px;
  padding: 8px 10px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${colors.grey.dark3};
  }
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${colors.black.dark1};
`;

const FormError = styled.p`
  margin: 14px 0 0;
  color: ${colors.red.default};
  font-size: 12px;
  font-weight: 600;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const SecondaryButton = styled.button`
  border: 1px solid ${colors.grey.dark3};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 10px 16px;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 0;
  background: ${colors.black.dark1};
  color: ${colors.white.default};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 10px 16px;
  cursor: pointer;

  &:disabled {
    background: ${colors.grey.dark6};
    cursor: not-allowed;
  }
`;

const NewClientButton = styled.button`
  border: 0;
  background: ${colors.black.dark1};
  color: ${colors.white.default};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 10px 16px;
  cursor: pointer;
`;

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const EmptyRow = styled.div`
  padding: 20px 16px;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

const TableGridBase = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 180px 140px 90px 100px 110px;
  gap: 8px;
  align-items: center;

  @media (max-width: 1200px) {
    grid-template-columns: 110px 1fr 160px 120px 90px 90px 100px;
  }
`;

const TableHeader = styled(TableGridBase)`
  padding: 12px 16px;
  color: ${colors.grey.dark6};
  border-bottom: 1px solid ${colors.grey.light3};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TableRow = styled(TableGridBase)`
  padding: 14px 16px;
  border-top: 1px solid ${colors.grey.light3};
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light6};
  }
`;

const IdPill = styled.span`
  border: 1px solid ${colors.grey.light4};
  background: ${colors.grey.light6};
  font-size: 11px;
  font-weight: 700;
  color: ${colors.black.light1};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 8px;
  width: fit-content;
`;

const NameCell = styled.span`
  font-size: 13px;
  color: ${colors.black.dark1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MutedCell = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItalicDash = styled.span`
  color: ${colors.grey.dark7};
  font-style: italic;
`;

const Right = styled.span<{ $strong?: boolean }>`
  text-align: right;
  font-size: 13px;
  color: ${colors.black.dark1};
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
`;

const RightSmall = styled.span`
  text-align: right;
  font-size: 11px;
  color: ${colors.grey.dark6};
`;
